import { EventType } from "@/analytics/analytics-events";
import { StructuredExportProperties } from "@/analytics/structured-export-event-properties";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import { selectCurrentUser } from "@/store/user-selectors";
import {
  Alert,
  FaroButton,
  FaroText,
  TextField,
  useToast,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import {
  assert,
  GUID,
  isValidFilename,
  VALID_CHARACTERS,
} from "@faro-lotv/foundation";
import { selectProjectId } from "@faro-lotv/project-source";
import {
  StructuredExportRequest,
  useApiClientContext,
} from "@faro-lotv/service-wires";
import { Stack } from "@mui/system";
import { useCallback, useEffect, useMemo, useState, type JSX } from "react";
import { returnToPreviousMode } from "../export-mode-utils";
import {
  DEFAULT_EXPORT_SETTINGS_VALUES,
  ScansExportSettings,
  ScansExportSettingsState,
  SUBSAMPLING_MAX_VALUE,
} from "./scans-export-settings";
import { selectResolvedSiteScapeIds } from "./scans-export-sitescape-utils";
import { useScansExportStore } from "./scans-export-store";
import { usePrepareScansExportData } from "./use-prepare-scans-export-data";

/** @returns The form required for the export of scans */
export function ScansExportForm(): JSX.Element | null {
  const projectId = useAppSelector(selectProjectId);
  const user = useAppSelector(selectCurrentUser);

  assert(projectId, "Project ID is required for scans export");
  assert(user, "User is required for scans export");

  const { datasetSection, scans } = usePrepareScansExportData();
  const { exportScans } = useScansExport();

  const selectedIds = useResolvedExportScanIds();

  const [fileName, setFileName] = useState(datasetSection.name);
  const [fileNameErrorText, setFileNameErrorText] = useState<string>();

  const [exportConfig, setExportConfig] = useState<ScansExportSettingsState>(
    DEFAULT_EXPORT_SETTINGS_VALUES,
  );

  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);

  // Update the file name state when the user changes it
  const updateFileName = useCallback((name: string) => {
    setFileName(name);

    // Validate the file name for length and characters for sanitization for immediate feedback
    if (name.length === 0) {
      setFileNameErrorText("File name cannot be empty");
      return;
    } else if (!isValidFilename(name)) {
      setFileNameErrorText(
        `The file name contains invalid characters. Allowed characters are: ${VALID_CHARACTERS}.`,
      );
      return;
    }

    // If the file name is valid, update the state
    setFileNameErrorText(undefined);
  }, []);

  // Validate the form whenever relevant fields change
  useEffect(() => {
    // Validate the overall form
    const isValid = validateScansExportForm(
      fileName,
      selectedIds,
      exportConfig.subsampling,
    );
    setIsFormValid(isValid);
  }, [exportConfig.subsampling, fileName, selectedIds]);

  const handleScansExport = useCallback(async () => {
    setHasSubmitted(true);
    Analytics.track<StructuredExportProperties>(
      EventType.exportStructuredPointCloud,
      {
        elementTypeHint: datasetSection.typeHint ?? undefined,
        hasEditedName: fileName !== datasetSection.name,
        areAllScansSelected: selectedIds.length === scans.size,
        hasIncluded360: exportConfig.includePanoramas,
        hasEnabledNonStandardRotation: exportConfig.enableNonStandardRotation,
        subsampling: exportConfig.subsampling,
        hasIncludeIntensity: exportConfig.includeIntensity,
        hasIncludedInvalidPoints: exportConfig.includeInvalidPoints,
        hasIncludedColor: exportConfig.includeColor,
      },
    );

    await exportScans(fileName, selectedIds, exportConfig);

    // Reset the submission state to allow re-submission
    setHasSubmitted(false);
  }, [
    datasetSection.name,
    datasetSection.typeHint,
    exportConfig,
    exportScans,
    fileName,
    scans.size,
    selectedIds,
  ]);

  return (
    <Stack sx={{ justifyContent: "space-between", height: "100%" }}>
      <Stack sx={{ gap: 2 }}>
        <FaroText variant="helpText">
          Export retains the original raw point cloud data unmodified,
          preserving the scan organization and metadata as found in the source
          dataset.
        </FaroText>
        <TextField
          label="File name"
          fullWidth
          text={fileName}
          onTextChanged={updateFileName}
          error={fileNameErrorText}
          placeholder="Enter file name"
        />

        <ScansExportSettings
          settings={exportConfig}
          onSettingsChange={setExportConfig}
        />
      </Stack>

      <Stack sx={{ gap: 2 }}>
        <Alert title="Note" variant="info">
          <FaroText variant="helpText">
            The exported scan will include the selected options if the
            corresponding data is present in the source.
          </FaroText>
        </Alert>
        <FaroButton
          onClick={handleScansExport}
          aria-label="start scans export"
          disabled={!isFormValid || hasSubmitted}
          isLoading={hasSubmitted}
        >
          Export
        </FaroButton>
      </Stack>
    </Stack>
  );
}

/**
 * Validates the structured export form inputs
 *
 * @param name - The export file name to validate
 * @param scanIds - Array of selected scan IDs to validate
 * @param subsampling - The subsampling factor to validate
 * @returns true if form is valid (name is non-empty with valid characters and at least one scan is selected), false otherwise
 */
function validateScansExportForm(
  name: string,
  scanIds: GUID[],
  subsampling: number,
): boolean {
  return (
    name.length > 0 &&
    isValidFilename(name) &&
    scanIds.length > 0 &&
    subsampling >= 1 &&
    subsampling <= SUBSAMPLING_MAX_VALUE
  );
}

interface UseScansExportResult {
  /** A callback to start an scans export*/
  exportScans(
    fileName: string,
    scans: GUID[],
    exportConfig: ScansExportSettingsState,
  ): Promise<void>;
}

/**
 * @returns A callback to start an scans export
 */
function useScansExport(): UseScansExportResult {
  const projectId = useAppSelector(selectProjectId);
  const user = useAppSelector(selectCurrentUser);

  assert(projectId, "Project ID is not available");
  assert(user, "User is not available");

  const { datasetSection, scans } = usePrepareScansExportData();

  const store = useAppStore();
  const dispatch = useAppDispatch();

  const { pointCloudApiClient } = useApiClientContext();

  const { handleErrorWithToast } = useErrorHandlers();
  const { openToast } = useToast();

  const exportScans = useCallback(
    async (
      fileName: string,
      selectedIds: GUID[],
      exportConfig: ScansExportSettingsState,
    ) => {
      // Prepare the payload for structured export
      const structuredExportPayload: StructuredExportRequest = {
        projectId,
        userId: user.id,
        elementId: datasetSection.id,
        format: "e57",
        outputFileName: fileName,
        includePanoramas: exportConfig.includePanoramas,
        enableNonStandardRotation: exportConfig.enableNonStandardRotation,
        includeIntensity: exportConfig.includeIntensity,
        includeInvalidPoints: exportConfig.includeInvalidPoints,
        includeColor: exportConfig.includeColor,
        includeIndices: exportConfig.includeIndices,
        subsampling: exportConfig.subsampling,
      };

      try {
        // Add scanIds only if not all the scans are selected, as the default behavior in the backend is to export all scans
        if (selectedIds.length !== scans.size) {
          structuredExportPayload.scanIds = selectedIds;
        }

        // Remove includePanoramas if false or undefined, as the default behavior in the backend is to not include panoramas
        if (!structuredExportPayload.includePanoramas) {
          delete structuredExportPayload.includePanoramas;
          // Also remove applyNonStandardRotation as it is only relevant if includePanoramas is true
          delete structuredExportPayload.enableNonStandardRotation;
        }

        await pointCloudApiClient.exportStructured(structuredExportPayload);
        openToast({
          title: "Preparing data for structured export",
          message:
            "You can track the export progress in Cloud activity and download the file once it's ready.",
        });
        returnToPreviousMode(store, dispatch);
      } catch (e) {
        handleErrorWithToast({
          title: "The export of the structured point cloud failed",
          error: `Something went wrong. Try again later. \n ${e}`,
        });
      }
    },
    [
      datasetSection.id,
      dispatch,
      handleErrorWithToast,
      openToast,
      pointCloudApiClient,
      projectId,
      scans.size,
      store,
      user.id,
    ],
  );

  return { exportScans };
}

/**
 * Resolves the user-selected IDs into concrete scan IDs for export.
 * Expands any abstract/grouped selections (e.g. clusters) into their individual scan IDs.
 *
 * @returns The resolved list of scan IDs ready for export
 */
function useResolvedExportScanIds(): GUID[] {
  const { selectedIds } = useScansExportStore();
  const { scans, invalidIds } = usePrepareScansExportData();

  // Filter out any invalid scan or cluster IDs that may have been selected as a safety net
  const validSelectedIds = useMemo(
    () => selectedIds.filter((id) => !invalidIds.has(id)),
    [selectedIds, invalidIds],
  );

  // Resolve any selected cluster IDs of SiteScape into their individual scan IDs for export, while leaving individual scan selections as is
  return useAppSelector(selectResolvedSiteScapeIds(validSelectedIds, scans));
}
